import React, { useState, useMemo } from 'react';
import { FiSearch, FiUser, FiDownload, FiFileText } from 'react-icons/fi';
import { HiOutlineUsers } from 'react-icons/hi2';
import useAPI from '../../hooks/useAPI';
import { getAdminLembagaScope, matchItemToAdminScope } from './adminScope';
import { exportRowsToExcel, exportRowsToPdf, formatDateForFilter, isWithinDateRange } from './exportUtilsAdmin';

function ListDonatur({ admin }) {
  const { data: donaturData, loading, error } = useAPI('/api/donatur');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const itemsPerPage = 10;

  const donaturArray = useMemo(() => {
    if (Array.isArray(donaturData)) {
      return donaturData;
    } else if (donaturData && donaturData.donatur && Array.isArray(donaturData.donatur)) {
      return donaturData.donatur;
    } else if (donaturData && donaturData.data && Array.isArray(donaturData.data)) {
      return donaturData.data;
    } else if (donaturData && donaturData.result && Array.isArray(donaturData.result)) {
      return donaturData.result;
    }
    return [];
  }, [donaturData]);

  const lembagaScope = useMemo(() => getAdminLembagaScope(admin), [admin]);
  const scopedDonaturArray = useMemo(
    () => donaturArray.filter((item) => matchItemToAdminScope(item, lembagaScope)),
    [donaturArray, lembagaScope]
  );

  // Filter berdasarkan search term
  const filteredDonatur = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return scopedDonaturArray.filter(item => {
      const nama = (item.nama || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      const noHp = (item.no_hp || item.nomor_hp || item.phone || '').toLowerCase();
      const kodeDonatur = (item.kode_donatur || item.kode || '').toLowerCase();
      const nik = (item.nik || item.no_identitas || '').toLowerCase();
      const agama = (item.agama || '').toLowerCase();
      const pekerjaan = (item.pekerjaan || item.profesi || '').toLowerCase();
      const tanggalDaftar =
        item.created_at ||
        item.tanggal_daftar ||
        item.tgl_daftar ||
        item.tanggal ||
        item.tgl;

      const matchesSearch =
        !searchTerm ||
        nama.includes(lowerSearch) || 
        email.includes(lowerSearch) || 
        noHp.includes(lowerSearch) ||
        kodeDonatur.includes(lowerSearch) ||
        nik.includes(lowerSearch) ||
        agama.includes(lowerSearch) ||
        pekerjaan.includes(lowerSearch);
      
      return matchesSearch && isWithinDateRange(tanggalDaftar, startDate, endDate);
    });
  }, [scopedDonaturArray, searchTerm, startDate, endDate]);

  // Pagination
  const totalPages = Math.ceil(filteredDonatur.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDonatur = filteredDonatur.slice(startIndex, startIndex + itemsPerPage);

  // Format tanggal
  const formatTanggal = (tanggal) => {
    if (!tanggal) return '-';
    return new Date(tanggal).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const sanitizeFilenamePart = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  const getExportMeta = () => {
    const queryPart = sanitizeFilenamePart(searchTerm);
    const startPart = startDate || 'awal';
    const endPart = endDate || 'akhir';
    const hasDateRange = Boolean(startDate || endDate);

    const filenameParts = ['laporan-donatur'];
    const titleParts = ['Laporan Donatur'];

    if (queryPart) {
      filenameParts.push(queryPart);
      titleParts.push(`Pencarian: ${searchTerm.trim()}`);
    }

    if (hasDateRange) {
      filenameParts.push(startPart, endPart);
      titleParts.push(`Periode ${startPart} s.d. ${endPart}`);
    }

    return {
      filename: filenameParts.join('-'),
      title: titleParts.join(' - '),
    };
  };

  const handleExportExcel = () => {
    const exportMeta = getExportMeta();

    exportRowsToExcel(
      exportMeta.filename,
      exportMeta.title,
      ['Kode Donatur', 'NIK', 'Nama', 'Tanggal Daftar', 'Tanggal Lahir', 'Jenis Kelamin', 'Agama', 'Pekerjaan', 'Email', 'No HP', 'Alamat'],
      filteredDonatur.map((item) => [
        item.kode_donatur || item.kode || '-',
        item.nik || item.no_identitas || '-',
        item.nama || '-',
        formatDateForFilter(item.created_at || item.tanggal_daftar || item.tgl_daftar || item.tanggal || item.tgl) || '-',
        formatTanggal(item.tanggal_lahir || item.tgl_lahir || '-'),
        item.jenis_kelamin || item.gender || '-',
        item.agama || '-',
        item.pekerjaan || item.profesi || '-',
        item.email || '-',
        item.no_hp || item.nomor_hp || item.phone || '-',
        item.alamat || '-',
      ])
    );
  };

  const handleExportPdf = () => {
    const exportMeta = getExportMeta();

    exportRowsToPdf(
      exportMeta.title,
      ['Kode Donatur', 'Nama', 'Tanggal Daftar', 'Email', 'No HP', 'Pekerjaan'],
      filteredDonatur.map((item) => [
        item.kode_donatur || item.kode || '-',
        item.nama || '-',
        formatDateForFilter(item.created_at || item.tanggal_daftar || item.tgl_daftar || item.tanggal || item.tgl) || '-',
        item.email || '-',
        item.no_hp || item.nomor_hp || item.phone || '-',
        item.pekerjaan || item.profesi || '-',
      ])
    );
  };

  const handleToggleExport = () => {
    setIsExportOpen((prev) => !prev);
  };

  const handleExportAction = (type) => {
    if (type === 'excel') {
      handleExportExcel();
    }

    if (type === 'pdf') {
      handleExportPdf();
    }

    setIsExportOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 w-full px-6 lg:px-16 py-12">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="relative mx-auto mb-5 w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
              <div className="animate-spin absolute inset-0 rounded-full border-4 border-blue-900 border-t-transparent"></div>
            </div>
            <p className="text-slate-600 font-medium">Memuat data donatur...</p>
            <p className="text-sm text-slate-400 mt-1">Mohon tunggu sebentar</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 w-full px-6 lg:px-16 py-12">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <span className="text-red-500 text-lg">!</span>
          </div>
          <div>
            <p className="text-red-700 font-semibold">Terjadi Kesalahan</p>
            <p className="text-red-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="w-full px-6 lg:px-16 py-10 space-y-8">

        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/30">
              <HiOutlineUsers className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 leading-tight">Daftar Donatur</h1>
              <p className="text-slate-500 text-sm mt-0.5">Kelola dan pantau seluruh data donatur yang terdaftar</p>
            </div>
          </div>

          {/* Summary badges */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2.5">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
              <span className="text-sm font-semibold text-blue-800">{scopedDonaturArray.length} Total Donatur</span>
            </div>
          </div>
        </div>

        {/* ── TOOLBAR ── */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* Toolbar header bar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-gradient-to-r from-blue-900 to-blue-800">
            <div className="flex items-center gap-2">
              <FiSearch className="text-blue-200 text-sm" />
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-100">Filter & Pencarian</span>
            </div>
            <span className="text-xs text-blue-200 font-medium">
              {filteredDonatur.length} donatur ditemukan
            </span>
          </div>

          {/* Toolbar body */}
          <div className="p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              {/* Left: Period + Search */}
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                {/* Period label */}
                <span className="shrink-0 text-sm font-semibold text-slate-600 hidden lg:block">Periode:</span>

                {/* Date range */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                    <span className="text-slate-400 text-sm">📅</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                      className="w-full min-w-[138px] bg-transparent text-sm text-slate-700 outline-none"
                    />
                  </div>

                  <span className="text-sm font-semibold text-slate-400 text-center">s/d</span>

                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                    <span className="text-slate-400 text-sm">📅</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                      className="w-full min-w-[138px] bg-transparent text-sm text-slate-700 outline-none"
                    />
                  </div>
                </div>

                {/* Search */}
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 shadow-sm transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                  <FiSearch className="h-4 w-4 flex-shrink-0 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari nama, email, nomor HP, NIK, agama, atau pekerjaan..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full min-w-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                      className="text-slate-400 hover:text-slate-600 transition-colors text-xs font-medium"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Export */}
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={handleToggleExport}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 hover:shadow-md active:scale-95"
                >
                  <FiDownload className="h-4 w-4" />
                  <span>Export</span>
                  <span className="text-xs opacity-70">{isExportOpen ? '▴' : '▾'}</span>
                </button>

                {isExportOpen && (
                  <div className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="px-3 py-2 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pilih Format</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleExportAction('excel')}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      <span className="text-base">🧾</span>
                      <div>
                        <p className="font-semibold">Export Excel</p>
                        <p className="text-xs text-slate-400">Format .xlsx</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExportAction('pdf')}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-rose-50 hover:text-rose-700"
                    >
                      <span className="text-base">📄</span>
                      <div>
                        <p className="font-semibold">Export PDF</p>
                        <p className="text-xs text-slate-400">Format .pdf</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── TABLE ── */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-900 text-white">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">No</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Kode Donatur</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">NIK</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Nama</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Tanggal Lahir</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Jenis Kelamin</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Agama</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Pekerjaan</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Email</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Nomor HP</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-blue-100 whitespace-nowrap">Alamat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedDonatur.length > 0 ? (
                  paginatedDonatur.map((donatur, index) => (
                    <tr
                      key={donatur.id || index}
                      className="hover:bg-blue-50/40 transition-colors duration-150 group"
                    >
                      {/* No */}
                      <td className="px-5 py-3.5 text-sm text-slate-400 font-medium whitespace-nowrap">
                        {startIndex + index + 1}
                      </td>

                      {/* Kode Donatur */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-100 text-blue-800 text-xs font-bold tracking-wide">
                          {donatur.kode_donatur || donatur.kode || '-'}
                        </span>
                      </td>

                      {/* NIK */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap font-mono">
                        {donatur.nik || donatur.no_identitas || '-'}
                      </td>

                      {/* Nama */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-sm">
                            {(donatur.nama || 'D').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-slate-800">
                            {donatur.nama || '-'}
                          </span>
                        </div>
                      </td>

                      {/* Tanggal Lahir */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {formatTanggal(donatur.tanggal_lahir || donatur.tgl_lahir || '-')}
                      </td>

                      {/* Jenis Kelamin */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {donatur.jenis_kelamin || donatur.gender || '-'}
                      </td>

                      {/* Agama */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {donatur.agama || '-'}
                      </td>

                      {/* Pekerjaan */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {donatur.pekerjaan || donatur.profesi || '-'}
                      </td>

                      {/* Email */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {donatur.email || '-'}
                      </td>

                      {/* Nomor HP */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                        {donatur.no_hp || donatur.nomor_hp || donatur.phone || '-'}
                      </td>

                      {/* Alamat */}
                      <td className="px-5 py-3.5 text-sm text-slate-500 max-w-[200px] truncate">
                        {donatur.alamat || '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                          <FiUser className="h-7 w-7 text-slate-300" />
                        </div>
                        <div>
                          <p className="text-slate-700 font-semibold">Tidak ada data donatur</p>
                          <p className="text-sm text-slate-400 mt-1">
                            {searchTerm || startDate || endDate
                              ? 'Tidak ada donatur yang sesuai dengan filter.'
                              : 'Mulai dengan menambahkan donatur baru.'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
              <p className="text-sm text-slate-500">
                Menampilkan{' '}
                <span className="font-semibold text-slate-700">{startIndex + 1}</span>
                {' '}–{' '}
                <span className="font-semibold text-slate-700">{Math.min(startIndex + itemsPerPage, filteredDonatur.length)}</span>
                {' '}dari{' '}
                <span className="font-semibold text-slate-700">{filteredDonatur.length}</span>
                {' '}donatur
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-white hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  ← Sebelumnya
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    )
                    .reduce((acc, page, idx, arr) => {
                      if (idx > 0 && page - arr[idx - 1] > 1) {
                        acc.push('...');
                      }
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((page, idx) =>
                      page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">…</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                            currentPage === page
                              ? 'bg-blue-900 text-white shadow-sm shadow-blue-900/30'
                              : 'border border-slate-200 text-slate-600 hover:bg-white hover:border-slate-300'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-white hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Berikutnya →
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default ListDonatur;
